import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '../../ui/Button/Button';
import './ProductDetailsDialog.css';

interface ProductDetailsQuestion {
  index: number;
  title: string;
  currentDetail: string;
  currentLink?: string;
}

interface ProductDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (details: { index: number; detail: string; link?: string }[]) => void;
  questions: ProductDetailsQuestion[];
}

export const ProductDetailsDialog: React.FC<ProductDetailsDialogProps> = ({
  isOpen,
  onClose,
  onComplete,
  questions
}) => {
  const { t } = useTranslation();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [details, setDetails] = useState<{ [key: number]: { detail: string; link?: string } }>({});
  const [currentDetail, setCurrentDetail] = useState('');
  const [currentLink, setCurrentLink] = useState('');

  useEffect(() => {
    if (isOpen && questions.length > 0) {
      setCurrentQuestionIndex(0);
      setDetails({});
      const firstQuestion = questions[0];
      setCurrentDetail(firstQuestion.currentDetail || '');
      setCurrentLink(firstQuestion.currentLink || '');
    }
  }, [isOpen, questions]);

  useEffect(() => {
    if (questions[currentQuestionIndex]) {
      const question = questions[currentQuestionIndex];
      const savedDetails = details[question.index];
      if (savedDetails) {
        setCurrentDetail(savedDetails.detail);
        setCurrentLink(savedDetails.link || '');
      } else {
        setCurrentDetail(question.currentDetail || '');
        setCurrentLink(question.currentLink || '');
      }
    }
  }, [currentQuestionIndex, questions, details]);

  if (!isOpen || questions.length === 0) return null;

  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  const handleNext = () => {
    // Save current details
    if (currentDetail.trim()) {
      setDetails(prev => ({
        ...prev,
        [currentQuestion.index]: {
          detail: currentDetail.trim(),
          link: currentLink.trim() || undefined
        }
      }));
    }

    if (isLastQuestion) {
      // Complete and submit
      const results = Object.entries(details).map(([indexStr, data]) => ({
        index: parseInt(indexStr),
        detail: data.detail,
        link: data.link
      }));
      
      // Add current detail if exists
      if (currentDetail.trim()) {
        results.push({
          index: currentQuestion.index,
          detail: currentDetail.trim(),
          link: currentLink.trim() || undefined
        });
      }
      
      onComplete(results);
    } else {
      // Move to next question
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handleSkip = () => {
    if (isLastQuestion) {
      // Complete without current detail
      const results = Object.entries(details).map(([indexStr, data]) => ({
        index: parseInt(indexStr),
        detail: data.detail,
        link: data.link
      }));
      onComplete(results);
    } else {
      // Move to next question without saving
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-content product-details-dialog" onClick={e => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>{t('productRegistration.detailsDialog.title')}</h2>
          <button className="dialog-close" onClick={onClose} aria-label={t('common.close')}>
            <X size={24} />
          </button>
        </div>

        <div className="dialog-body">
          <div className="question-progress">
            <span>{t('productRegistration.detailsDialog.progress', { 
              current: currentQuestionIndex + 1, 
              total: questions.length 
            })}</span>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="question-content">
            <h3>{t('productRegistration.detailsDialog.productTitle', {
              title: currentQuestion.title
            })}</h3>

            <div className="form-fields">
              <div className="form-field">
                <label>{t('productRegistration.detailsDialog.detailLabel')}</label>
                <textarea
                  value={currentDetail}
                  onChange={(e) => setCurrentDetail(e.target.value)}
                  placeholder={t('productRegistration.detailsDialog.detailPlaceholder')}
                  rows={4}
                  className="detail-textarea"
                  autoFocus
                />
              </div>

              <div className="form-field">
                <label>{t('productRegistration.detailsDialog.linkLabel')}</label>
                <input
                  type="url"
                  value={currentLink}
                  onChange={(e) => setCurrentLink(e.target.value)}
                  placeholder={t('productRegistration.detailsDialog.linkPlaceholder')}
                  className="link-input"
                />
              </div>
            </div>

            <div className="question-hint">
              {t('productRegistration.detailsDialog.hint')}
            </div>
          </div>
        </div>

        <div className="dialog-footer">
          <Button
            variant="secondary"
            onClick={handleSkip}
          >
            {isLastQuestion 
              ? t('productRegistration.detailsDialog.skipAndComplete')
              : t('productRegistration.detailsDialog.skip')
            }
          </Button>
          <Button
            variant="primary"
            onClick={handleNext}
            disabled={!currentDetail.trim()}
          >
            {isLastQuestion 
              ? t('productRegistration.detailsDialog.complete')
              : t('productRegistration.detailsDialog.next')
            }
          </Button>
        </div>
      </div>
    </div>
  );
};